import type { CartLine, CommandKind, CommandPayloads, ModifierGroupRule } from '@didto/domain';
import { lineTotalMinor } from '@didto/domain';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useSession } from '@/app/session';
import { useToast } from '@/app/toast';
import { fetchStationHealth } from '@/lib/api';
import type { CommandResult, StationHealth } from '@/lib/api';
import {
  catalogKeys,
  fetchAssignments,
  fetchAvailability,
  fetchCategories,
  fetchModifierGroups,
  fetchModifierOptions,
  fetchProducts,
  fetchVariants,
} from '@/lib/catalog';
import type { Category, ModifierGroup, ModifierOption, Product, Variant } from '@/lib/catalog';
import type { Row } from '@/lib/db';
import { toAppError } from '@/lib/errors';
import type { AppError } from '@/lib/errors';
import { runQueuedCommand } from '@/lib/outbox';
import { supabase } from '@/lib/supabase';

/**
 * Data and helpers behind the POS: who is ordering and where, the catalog shaped for tiles and
 * the picker, tables, approvers, and the command runner that routes writes through the outbox.
 */

/* ── Terminal context ───────────────────────────────────────────────────────────────────── */

export interface PosContext {
  principal: 'human' | 'device' | 'none';
  locationId: string;
  timeZone: string;
  currency: string;
  employeeId: string | null;
  /** The terminal an order belongs to: the device itself, or the terminal a human picked. */
  terminal: { id: string; code: string; name: string } | null;
  /** Terminals a human session may act as (empty for devices). */
  terminals: TerminalOption[];
}

export type TerminalOption = Pick<
  Row<'terminal'>,
  'id' | 'name' | 'code' | 'kind' | 'status' | 'cash_drawer_id'
>;

async function rows<T>(q: PromiseLike<{ data: T[] | null; error: unknown }>): Promise<T[]> {
  const { data, error } = await q;
  if (error) throw error;
  return data ?? [];
}

const choiceListeners = new Set<() => void>();
const choiceKey = (locationId: string) => `didto-os-pos-terminal:${locationId}`;
function readTerminalChoice(locationId: string): string | null {
  try {
    return localStorage.getItem(choiceKey(locationId));
  } catch {
    return null;
  }
}
/** A human's chosen terminal for this browser, per location (refinement 4). Never trusted server-side. */
export function setTerminalChoice(locationId: string, terminalId: string | null) {
  try {
    if (terminalId) localStorage.setItem(choiceKey(locationId), terminalId);
    else localStorage.removeItem(choiceKey(locationId));
  } catch {
    // storage unavailable (private mode): the choice lives for this render only
  }
  choiceListeners.forEach((l) => l());
}
function subscribeChoice(listener: () => void) {
  choiceListeners.add(listener);
  return () => {
    choiceListeners.delete(listener);
  };
}
export function useTerminalChoice(locationId: string): string | null {
  const get = useCallback(() => readTerminalChoice(locationId), [locationId]);
  return useSyncExternalStore(subscribeChoice, get, () => null);
}

export function usePosContext(): PosContext {
  const s = useSession();
  const me = s.me;
  const principal = me?.principal ?? 'none';
  const locationId =
    principal === 'device' ? (me?.terminal?.location_id ?? '') : (me?.active_location_id ?? '');
  const timeZone =
    me?.locations.find((l) => l.id === locationId)?.timezone ??
    me?.business?.timezone ??
    'Asia/Manila';
  const currency = me?.business?.currency ?? 'PHP';
  const employeeId =
    principal === 'device'
      ? (me?.terminal_session?.employee_id ?? null)
      : (me?.employee?.id ?? null);

  const terminals = useQuery({
    queryKey: ['terminals', 'pos', locationId],
    enabled: principal === 'human' && !!locationId,
    staleTime: 60_000,
    queryFn: () =>
      rows<TerminalOption>(
        supabase
          .from('terminal')
          .select('id, name, code, kind, status, cash_drawer_id')
          .eq('location_id', locationId)
          .is('archived_at', null)
          .neq('status', 'revoked')
          .order('code'),
      ),
  });
  const choice = useTerminalChoice(locationId);

  let terminal: PosContext['terminal'] = null;
  if (principal === 'device' && me?.terminal)
    terminal = { id: me.terminal.id, code: me.terminal.code, name: me.terminal.name };
  else if (principal === 'human') {
    const t = (terminals.data ?? []).find((x) => x.id === choice);
    if (t) terminal = { id: t.id, code: t.code, name: t.name };
  }
  // No manual memo: the React Compiler memoizes this shape from its inputs.
  return {
    principal,
    locationId,
    timeZone,
    currency,
    employeeId,
    terminal,
    terminals: principal === 'human' ? (terminals.data ?? []) : [],
  };
}

/* ── Catalog shaped for the POS ─────────────────────────────────────────────────────────── */

export interface PosGroup {
  group: ModifierGroup;
  options: ModifierOption[];
  rule: ModifierGroupRule;
}
export interface PosProduct {
  product: Product;
  /** Live variants in sort order (the hidden default first when unnamed). */
  variants: Variant[];
  groups: PosGroup[];
  soldOutVariantIds: ReadonlySet<string>;
  soldOut: boolean;
  fromMinor: number;
}
export interface PosCatalog {
  loading: boolean;
  error: string | null;
  categories: Category[];
  products: PosProduct[];
  /** Read again what could not be read (the catalog's error state offers it). */
  retry: () => void;
}

const STALE = 60_000;

export function usePosCatalog(locationId: string): PosCatalog {
  const categories = useQuery({
    queryKey: catalogKeys.categories,
    queryFn: fetchCategories,
    staleTime: STALE,
  });
  const products = useQuery({
    queryKey: catalogKeys.products,
    queryFn: fetchProducts,
    staleTime: STALE,
  });
  const variants = useQuery({
    queryKey: catalogKeys.variants,
    queryFn: fetchVariants,
    staleTime: STALE,
  });
  const groups = useQuery({
    queryKey: catalogKeys.groups,
    queryFn: fetchModifierGroups,
    staleTime: STALE,
  });
  const options = useQuery({
    queryKey: catalogKeys.options,
    queryFn: fetchModifierOptions,
    staleTime: STALE,
  });
  const assignments = useQuery({
    queryKey: catalogKeys.assignments,
    queryFn: fetchAssignments,
    staleTime: STALE,
  });
  const availability = useQuery({
    queryKey: catalogKeys.availability,
    queryFn: fetchAvailability,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
  const all = [categories, products, variants, groups, options, assignments, availability];
  const loading = all.some((q) => q.isLoading);
  const firstError = all.find((q) => q.error)?.error;
  const error = firstError ? toAppError(firstError).message : null;
  const retry = () => {
    for (const q of all) if (q.error) void q.refetch();
  };

  const shaped = useMemo<Omit<PosCatalog, 'retry'>>(() => {
    const live = <T extends { archived_at: string | null }>(xs: T[] | undefined): T[] =>
      (xs ?? []).filter((x) => !x.archived_at);
    const variantsByProduct = new Map<string, Variant[]>();
    for (const v of live(variants.data)) {
      const list = variantsByProduct.get(v.product_id) ?? [];
      list.push(v);
      variantsByProduct.set(v.product_id, list);
    }
    const optionsByGroup = new Map<string, ModifierOption[]>();
    for (const o of live(options.data)) {
      const list = optionsByGroup.get(o.modifier_group_id) ?? [];
      list.push(o);
      optionsByGroup.set(o.modifier_group_id, list);
    }
    const groupsById = new Map(live(groups.data).map((g) => [g.id, g] as const));
    const unavailable = new Set(
      (availability.data ?? [])
        .filter((a) => a.location_id === locationId && !a.is_available)
        .map((a) => a.product_variant_id),
    );
    const shaped: PosProduct[] = [];
    for (const p of live(products.data)) {
      const vs = [...(variantsByProduct.get(p.id) ?? [])].sort((a, b) => a.sort - b.sort);
      if (vs.length === 0) continue;
      const gs: PosGroup[] = [];
      for (const a of (assignments.data ?? [])
        .filter((x) => x.product_id === p.id)
        .sort((x, y) => x.sort - y.sort)) {
        const g = groupsById.get(a.modifier_group_id);
        if (!g) continue;
        const opts = [...(optionsByGroup.get(g.id) ?? [])].sort((x, y) => x.sort - y.sort);
        if (opts.length === 0 && g.min_select === 0) continue;
        gs.push({
          group: g,
          options: opts,
          rule: {
            id: g.id,
            name: g.name,
            min_select: g.min_select,
            max_select: g.max_select,
            option_ids: opts.map((o) => o.id),
          },
        });
      }
      const soldOutVariantIds = new Set(vs.filter((v) => unavailable.has(v.id)).map((v) => v.id));
      const orderable = vs.filter((v) => !soldOutVariantIds.has(v.id));
      const priced = orderable.length ? orderable : vs;
      shaped.push({
        product: p,
        variants: vs,
        groups: gs,
        soldOutVariantIds,
        soldOut: orderable.length === 0,
        fromMinor: Math.min(...priced.map((v) => v.price_minor)),
      });
    }
    return {
      loading,
      error,
      categories: [...live(categories.data)].sort((a, b) => a.sort - b.sort),
      products: shaped,
    };
  }, [
    loading,
    error,
    locationId,
    categories.data,
    products.data,
    variants.data,
    groups.data,
    options.data,
    assignments.data,
    availability.data,
  ]);
  return { ...shaped, retry };
}

/** A product that needs no choices goes straight into the cart with one tap. */
export function needsPicker(p: PosProduct): boolean {
  return (
    p.variants.length > 1 || p.groups.some((g) => g.options.length > 0 || g.rule.min_select > 0)
  );
}

/* ── Cart lines as the UI holds them (ids for the server, names and prices for display) ── */

export interface UiModifier {
  modifier_option_id: string;
  group_name: string;
  name: string;
  price_delta_minor: number;
}
export interface UiLine {
  id: string;
  product_id: string;
  product_variant_id: string;
  name: string;
  variant_name: string | null;
  unit_price_minor: number;
  qty: number;
  notes: string;
  modifiers: UiModifier[];
}

export const uiLineTotal = (l: UiLine): number =>
  lineTotalMinor(
    l.unit_price_minor,
    l.modifiers.map((m) => m.price_delta_minor),
    l.qty,
  );

export function toCartLine(l: UiLine): CartLine {
  const notes = l.notes.trim();
  return {
    id: l.id,
    product_variant_id: l.product_variant_id,
    qty: l.qty,
    notes: notes ? notes : null,
    modifiers: l.modifiers.map((m) => ({ modifier_option_id: m.modifier_option_id, qty: 1 })),
  };
}

/** Same variant, same options, same notes: one line with a higher quantity. */
export function sameChoice(a: UiLine, b: UiLine): boolean {
  if (a.product_variant_id !== b.product_variant_id) return false;
  if (a.notes.trim() !== b.notes.trim()) return false;
  const ids = (l: UiLine) =>
    l.modifiers
      .map((m) => m.modifier_option_id)
      .sort()
      .join(',');
  return ids(a) === ids(b);
}

export function describeLine(l: { variant_name: string | null; modifiers: { name: string }[] }) {
  return [l.variant_name, ...l.modifiers.map((m) => m.name)].filter(Boolean).join(' · ');
}

/* ── Floor and people ───────────────────────────────────────────────────────────────────── */

export type PosTable = Pick<Row<'dining_table'>, 'id' | 'name' | 'area_id' | 'seats'>;
export function useTables(locationId: string) {
  return useQuery({
    queryKey: ['tables', 'pos', locationId],
    enabled: !!locationId,
    staleTime: STALE,
    queryFn: () =>
      rows<PosTable>(
        supabase
          .from('dining_table')
          .select('id, name, area_id, seats')
          .eq('location_id', locationId)
          .is('archived_at', null)
          .order('sort'),
      ),
  });
}

export type PosStation = Pick<Row<'production_station'>, 'id' | 'code' | 'name' | 'color'>;
export function useStations(locationId: string) {
  return useQuery({
    queryKey: ['stations', 'pos', locationId],
    enabled: !!locationId,
    staleTime: STALE,
    queryFn: () =>
      rows<PosStation>(
        supabase
          .from('production_station')
          .select('id, code, name, color')
          .eq('location_id', locationId)
          .is('archived_at', null)
          .order('sort'),
      ),
  });
}

/** Phase 5 station health: which stations have a KDS screen and whether it heartbeats. */
export function useStationHealth(locationId: string, principal: PosContext['principal']) {
  return useQuery({
    queryKey: ['station-health', locationId],
    enabled: !!locationId && principal !== 'none',
    staleTime: 30_000,
    refetchInterval: 60_000,
    queryFn: () => fetchStationHealth(principal === 'human' ? locationId : undefined),
  });
}

/** "Kitchen screen offline for 12 min", measured with the server clock. */
export function describeOffline(h: StationHealth): string {
  if (!h.last_seen_at) return `${h.name} screen has never connected`;
  const minutes = Math.max(
    0,
    Math.floor((Date.parse(h.server_time) - Date.parse(h.last_seen_at)) / 60_000),
  );
  return `${h.name} screen offline for ${minutes} min`;
}

/* ── Command runner ─────────────────────────────────────────────────────────────────────── */

export type RunOutcome = { ok: true; result: CommandResult } | { ok: false; error: AppError };

/**
 * Send a command through the outbox and refresh order queries. Errors are toasted unless the
 * caller's `handle` takes them (approval prompts, conflicts). A conflict always refetches the
 * order so the cashier sees what changed on the other device.
 */
export function useRun(ctx: Pick<PosContext, 'principal' | 'locationId'>) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const locationId = ctx.principal === 'human' ? ctx.locationId : '';
  return useCallback(
    async <K extends CommandKind>(
      kind: K,
      payload: CommandPayloads[K],
      opts: { handle?: (e: AppError) => boolean } = {},
    ): Promise<RunOutcome> => {
      try {
        const result = await runQueuedCommand(kind, payload, locationId ? { locationId } : {});
        await qc.invalidateQueries({ queryKey: ['orders'] });
        // placing, moving, cancelling or voiding a dine-in order changes table occupancy (ADR-021)
        void qc.invalidateQueries({ queryKey: ['table-sessions'] });
        return { ok: true, result };
      } catch (e) {
        const error = toAppError(e);
        if (error.code === 'conflict') void qc.invalidateQueries({ queryKey: ['orders'] });
        if (!opts.handle?.(error)) toast(error.message, 'danger');
        return { ok: false, error };
      }
    },
    [locationId, qc, toast],
  );
}
